// The pins a chat put on the board, as the chat shows them: each note's words
// and the trace it stands on, under the call that pinned it, and kept in view
// when the chat folds its work away. A pin here and its pin on the board point
// at each other, as a walkthrough's moments and their pins do: pointing at one
// lights its pin on the board in front, a click opens it there, and a pin the
// board's pointer is on lights its line here (lib/workspaceStore.js).
import { useEffect, useRef } from "react";
import { openWorkspaceNote, pointWorkspaceNote, useWorkspaceCanvas, useWorkspaceNotesPointed } from "../lib/workspaceStore.js";

const lower = (v) => String(v ?? "").trim().toLowerCase();

/**
 * The pins a finished call put on the board, or null for any other call:
 * [{ id, session, text, ref?, trace?, turn?, lane? }], `id` the note's on the
 * board. The chat's `workspace_point` pins one, as its note `note:<call id>`.
 */
export function chatPinsOf(node) {
  if (node?.kind !== "tool" || node.status !== "done") return null;
  const d = node.details;
  if (d?.kind === "workspacePoint" && d.session && d.note && node.id) {
    return [{ id: `note:${node.id}`, session: lower(d.session), text: String(d.note), turn: Number.isInteger(d.turn) ? d.turn : null, lane: d.lane || null }];
  }
  return null;
}

// What a folded run's pins say over them, where the calls are folded away.
export const pinsHeading = (n) => `Pinned ${n} note${n === 1 ? "" : "s"} on the board`;

// A note's pin as the board draws one: a head in ink with a dot.
function PinMark() {
  return (
    <span aria-hidden="true" className="mt-[1px] grid h-[14px] w-[14px] shrink-0 place-items-center rounded-full bg-[color:var(--ink)]" data-chat-pin-mark="">
      <span className="h-[5px] w-[5px] rounded-full bg-[color:var(--bg)]" />
    </span>
  );
}

/**
 * `pins`, as chatPinsOf gives them; `heading`, a line over them where the
 * call that pinned them is not shown (a folded run of work).
 */
export default function ChatPinsCard({ pins, heading = null }) {
  const board = useWorkspaceCanvas();
  const pointed = useWorkspaceNotesPointed();
  // The pin pointed at from here, let go of if this goes away under the pointer.
  const held = useRef(null);
  useEffect(() => () => { if (held.current) pointWorkspaceNote(null); }, []);
  if (!pins?.length) return null;
  const titleOf = (session) => (board?.cards || []).find((c) => lower(c.id) === session)?.title || null;
  const point = (id) => {
    held.current = id;
    pointWorkspaceNote(id);
  };
  return (
    <div className="grid min-w-0 gap-[2px]" data-chat-pins="">
      {heading ? <div className="px-[6px] pb-[2px] text-[12px] leading-[1.3] text-[color:var(--muted)]" data-chat-pins-heading="">{heading}</div> : null}
      {pins.map((p) => {
        const lit = !!pointed?.includes(p.id);
        const where = [p.ref, titleOf(lower(p.session)) || p.trace || null, p.turn != null ? `T${p.turn}` : null, p.lane].filter(Boolean).join(" \u00b7 ");
        return (
          <button
            key={p.id}
            type="button"
            data-chat-pin={p.id}
            data-lit={lit ? "" : undefined}
            onMouseEnter={() => point(p.id)}
            onMouseLeave={() => point(null)}
            onFocus={() => point(p.id)}
            onBlur={() => point(null)}
            onClick={() => openWorkspaceNote(p.id)}
            className={`flex w-full min-w-0 cursor-pointer items-start gap-[8px] rounded-[6px] px-[6px] py-[5px] text-left outline-none transition-colors focus-visible:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)] ${lit ? "bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : "hover:bg-[color-mix(in_srgb,var(--ink)_8%,transparent)]"}`}
          >
            <PinMark />
            <span className="grid min-w-0 flex-1 gap-[1px]">
              <span className="text-[12px] leading-[16px] text-[color:var(--ink)] [overflow-wrap:anywhere]">{p.text}</span>
              {where ? <span className="truncate text-[11px] leading-[14px] text-[color:var(--muted)]" data-chat-pin-where="">{where}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
